import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, Check, CircleDashed } from 'lucide-react'
import type { VenuePipelineDto } from '@lib/dto/pipeline.dto'
import { FUNNEL_TARGET_LABELS } from '@lib/enums/funnel-stage.enum'
import { Card } from '@/components/ui/card'
import { formatCount } from '@/lib/adFormat'
import { joinNames } from '@/lib/joinNames'
import { instantLabel } from '@/lib/instantLabel'
import { connectableText, type CompanyConnections } from './pipelineText'

type TrackingData = Pick<VenuePipelineDto, 'venueId' | 'tracking' | 'stages' | 'updatedAt' | 'honeybookUpdatedAt'>

interface TrackingPanelProps {
  data: TrackingData
  /** Whether this person sets up the venue's integrations: the fixes link to its settings only then. */
  canSetUp: boolean
  /** What the company has switched on: a venue can only connect what it has. */
  companyHas: CompanyConnections
  /** Shows the couples with no lead behind them. */
  onUnlinked: () => void
}

function Note({ tone, children }: { tone: 'ok' | 'attention' | 'off'; children: ReactNode }) {
  const icon = {
    ok: <Check aria-label="Working" className="mt-0.5 h-4 w-4 shrink-0 text-ok-ink" />,
    attention: <AlertTriangle aria-label="Needs a look" className="mt-0.5 h-4 w-4 shrink-0 text-hold-ink" />,
    off: <CircleDashed aria-label="Not tracked" className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" />,
  }[tone]
  return (
    <li className="flex gap-3 border-t border-line py-3 text-sm text-ink">
      {icon}
      <p className="m-0">{children}</p>
    </li>
  )
}

function Fix({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="font-medium text-accent-ink hover:underline">
      {children}
    </Link>
  )
}

/**
 * What would bring the inquiries in, said for what the company has: a link to
 * the venue's section for it, for whoever sets up integrations; nothing at a
 * company with neither, which can't connect one.
 */
function ConnectFix({ settings, canSetUp, companyHas }: { settings: string; canSetUp: boolean; companyHas: CompanyConnections }) {
  const which = connectableText(companyHas)
  if (which === null) return null
  if (!canSetUp) return <>Connecting {which} counts every inquiry it holds.</>
  return <Fix to={`${settings}#${companyHas.gohighlevel ? 'gohighlevel' : 'honeybook'}`}>Connect {which}</Fix>
}

/**
 * What the numbers can't see (docs/pipeline-view.spec.md, "Tracking"), each
 * with what would fix it, in place of a dashed "Not tracked" row per step: where
 * inquiries come in from, lead sources nobody has said the meaning of, bookings
 * with no lead, and the steps nothing records.
 */
export function TrackingPanel({ data, canSetUp, companyHas, onUnlinked }: TrackingPanelProps) {
  const tracking = data.tracking
  if (tracking === null) return null
  const settings = `/venues/${data.venueId}/settings`
  const ghl = tracking.connections.includes('gohighlevel')
  const notTracked = data.stages.filter((s) => !s.tracked && s.target !== 'lost' && s.target !== 'canceled').map((s) => FUNNEL_TARGET_LABELS[s.target])
  const unmapped = tracking.unmappedSources
  const unmappedInquiries = tracking.unmappedInquiries
  const reads = [
    data.updatedAt === null ? null : `GoHighLevel updated ${instantLabel(data.updatedAt)}`,
    data.honeybookUpdatedAt === null ? null : `HoneyBook updated ${instantLabel(data.honeybookUpdatedAt)}`,
  ].filter((r): r is string => r !== null)

  return (
    <Card asChild>
      <section id="tracking" aria-labelledby="tracking-heading" className="flex min-w-0 flex-[1_1_20rem] flex-col p-5">
        <h2 id="tracking-heading" className="m-0 text-heading font-semibold text-ink">
          Tracking
        </h2>
        <p className="m-0 mb-2 text-small text-ink-muted">What these numbers can and can’t see</p>
        <ul className="m-0 list-none p-0">
          {tracking.connections.length === 0 ? (
            <Note tone="attention">
              Inquiries here are counted only from the events made in FLOH Venues.{' '}
              <ConnectFix settings={settings} canSetUp={canSetUp} companyHas={companyHas} />
            </Note>
          ) : (
            <Note tone="ok">{reads.length === 0 ? 'Reading its leads for the first time.' : `${reads.join(' · ')}.`}</Note>
          )}
          {unmapped.length === 0 ? null : (
            <Note tone="attention">
              {unmapped.length === 1 ? `The lead source “${unmapped[0]?.name ?? ''}” doesn’t` : `${formatCount(unmapped.length)} lead sources don’t`} say where
              couples came from yet, so {formatCount(unmappedInquiries)} {unmappedInquiries === 1 ? 'inquiry counts' : 'inquiries count'} as Unknown.{' '}
              {canSetUp ? <Fix to={`${settings}#sources`}>Say what they mean</Fix> : null}
            </Note>
          )}
          {tracking.bookingsWithoutLead === 0 ? null : (
            <Note tone="attention">
              {formatCount(tracking.bookingsWithoutLead)} {tracking.bookingsWithoutLead === 1 ? 'booking isn’t' : 'bookings aren’t'} joined to a lead, so
              where {tracking.bookingsWithoutLead === 1 ? 'it' : 'they'} came from is Unknown.{' '}
              <button type="button" onClick={onUnlinked} className="font-medium text-accent-ink hover:underline focus-visible:focus-ds">
                See the couples with no lead
              </button>
            </Note>
          )}
          {notTracked.length === 0 ? null : (
            <Note tone="off">
              {joinNames(notTracked)} {notTracked.length === 1 ? 'isn’t' : 'aren’t'} tracked here.{' '}
              {ghl && canSetUp ? <Fix to={`${settings}#gohighlevel`}>Choose the GoHighLevel stage or tag for each</Fix> : null}
              {!ghl && companyHas.gohighlevel ? 'Connecting GoHighLevel can track them.' : null}
            </Note>
          )}
        </ul>
      </section>
    </Card>
  )
}
